<template>
	<div class="orders-group-header">
		<Text
			ref="heading"
			class="orders-group-header__title"
			font="--text-18-22-med"
			color="--text-dark-primary"
			role="heading"
			aria-level="2"
			tabindex="-1"
		>
			{{ title }}
		</Text>
		<Badge tone="dark" size="xs">{{ count }}</Badge>
	</div>
</template>

<script setup lang="ts">
import { ref, type ComponentPublicInstance } from 'vue'
import { Badge, Text } from '@simple-life-app/growth-ui-kova'

/**
 * A group title on the Orders list (Figma 13715:74905 "In progress" / "All orders"): the level-2 heading and
 * the group's count in the DS dark pill. The count is whatever the host passes — the list's totals, never a
 * page's size (docs/architecture/orders.md L3).
 */

defineProps<{
	title: string
	count: number
}>()

const heading = ref<ComponentPublicInstance | null>(null)

defineExpose({
	/**
	 * Take the focus where the group is, without scrolling — after a list retry whose button went with the card
	 * (OrdersSection). A user who scrolled while the retry read stays where they scrolled to.
	 */
	focus: () => (heading.value?.$el as HTMLElement | undefined)?.focus({ preventScroll: true }),
	/**
	 * Take the focus and bring the group to the top of the screen — after a page change of "All orders", so a
	 * keyboard or screen-reader user starts reading the new page from its heading. jsdom has no
	 * `scrollIntoView`, hence the optional call.
	 */
	reveal: () => {
		const el = heading.value?.$el as HTMLElement | undefined
		el?.focus({ preventScroll: true })
		el?.scrollIntoView?.({ block: 'start' })
	},
})
</script>

<style scoped>
/* Figma: the title and the 26px count pill 8 apart, centred on one line. */
.orders-group-header {
	display: flex;
	align-items: center;
	gap: var(--size-8);
}

/* A focus target for a page change or a list retry only (tabindex -1), not a control: no ring. */
.orders-group-header__title:focus {
	outline: none;
}
</style>
